import React, { useEffect, useState, useRef, useCallback } from "react";
import { Sidebar } from "./Sidebar";
import { Header } from "./Header";

export function AppShell({ title, subtitle, headerActions, polling = false, lastUpdatedMs = null, children }) {
  return (
    <div className="flex min-h-dvh bg-background">
      <div className="hidden lg:block">
        <Sidebar />
      </div>
      <main className="flex-1 flex flex-col min-w-0">
        <Header title={title} subtitle={subtitle} actions={headerActions} polling={polling} lastUpdatedMs={lastUpdatedMs} />
        <div className="flex-1 min-w-0 px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
          {children}
        </div>
      </main>
    </div>
  );
}

// Polling hook — calls fn every intervalMs, returns { data, loading, polling, lastUpdated, refresh }
export function usePolling(fn, intervalMs = 15000) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [polling, setPolling] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const mountedRef = useRef(true);
  const fnRef = useRef(fn);

  useEffect(() => { fnRef.current = fn; }, [fn]);

  const tick = useCallback(async (silent = false) => {
    if (!silent) setPolling(true);
    try {
      const res = await fnRef.current();
      if (mountedRef.current) {
        setData(res);
        setLastUpdated(Date.now());
      }
    } catch (e) {
      // ignore; caller can check
    } finally {
      if (mountedRef.current) {
        setLoading(false);
        setPolling(false);
      }
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    tick();
    const id = setInterval(() => tick(true), intervalMs);
    return () => {
      mountedRef.current = false;
      clearInterval(id);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intervalMs]);

  return { data, loading, polling, lastUpdated, refresh: () => tick(false) };
}
